import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Plus, Edit, Trash2, Loader2, Briefcase } from "lucide-react";
import { format } from "date-fns";
import type { ContactEmployment } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";

const EMPLOYMENT_STATUS_OPTIONS = [
  { value: "full_time", label: "Full Time" },
  { value: "part_time", label: "Part Time" },
  { value: "casual", label: "Casual" },
  { value: "contract", label: "Contract" },
  { value: "self_employed", label: "Self Employed" },
  { value: "unemployed", label: "Unemployed" },
  { value: "retired", label: "Retired" },
  { value: "student", label: "Student" },
  { value: "home_duties", label: "Home Duties" },
];

const SALARY_FREQUENCY_OPTIONS = [
  { value: "weekly", label: "Weekly" },
  { value: "fortnightly", label: "Fortnightly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annually", label: "Annually" },
];

interface EmploymentPanelProps {
  contactId: string;
}

interface FormData {
  employmentStatus: string;
  employerName: string;
  employerAbn: string;
  employerIndustry: string;
  jobTitle: string;
  department: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  baseSalary: string;
  salaryFrequency: string;
  isCurrent: boolean;
}

const initialFormData: FormData = {
  employmentStatus: "",
  employerName: "",
  employerAbn: "",
  employerIndustry: "",
  jobTitle: "",
  department: "",
  startDate: undefined,
  endDate: undefined,
  baseSalary: "",
  salaryFrequency: "",
  isCurrent: false,
};

export function EmploymentPanel({ contactId }: EmploymentPanelProps) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingEmployment, setEditingEmployment] = useState<ContactEmployment | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const { toast } = useToast();

  const { data: employment = [], isLoading } = useQuery<ContactEmployment[]>({
    queryKey: ["/api/crm/contacts", contactId, "employment"],
  });

  const addMutation = useMutation({
    mutationFn: async (data: Partial<ContactEmployment>) => {
      await apiRequest("POST", `/api/crm/contacts/${contactId}/employment`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/contacts", contactId, "employment"] });
      toast({ title: "Employment added", description: "The employment record has been added successfully." });
      handleCloseDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to add employment", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/crm/contacts/${contactId}/employment/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/contacts", contactId, "employment"] });
      toast({ title: "Employment removed", description: "The employment record has been removed." });
      setDeleteId(null);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to remove employment", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: { id: string; payload: Partial<ContactEmployment> }) => {
      await apiRequest("PATCH", `/api/crm/contacts/${contactId}/employment/${data.id}`, data.payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/contacts", contactId, "employment"] });
      toast({ title: "Employment updated", description: "The employment record has been updated successfully." });
      handleCloseEditDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to update employment", variant: "destructive" });
    },
  });

  const handleCloseDialog = () => {
    setIsAddDialogOpen(false);
    setFormData(initialFormData);
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditingEmployment(null);
    setFormData(initialFormData);
  };

  const handleOpenEdit = (emp: ContactEmployment) => {
    setEditingEmployment(emp);
    setIsEditDialogOpen(true);
  };

  // useEffect to pre-populate form when editing
  useEffect(() => {
    if (editingEmployment && isEditDialogOpen) {
      setFormData({
        employmentStatus: editingEmployment.employmentStatus || "",
        employerName: editingEmployment.employerName || "",
        employerAbn: editingEmployment.employerAbn || "",
        employerIndustry: editingEmployment.employerIndustry || "",
        jobTitle: editingEmployment.jobTitle || "",
        department: editingEmployment.department || "",
        startDate: editingEmployment.startDate ? new Date(editingEmployment.startDate) : undefined,
        endDate: editingEmployment.endDate ? new Date(editingEmployment.endDate) : undefined,
        baseSalary: editingEmployment.baseSalary || "",
        salaryFrequency: editingEmployment.salaryFrequency || "",
        isCurrent: editingEmployment.isCurrent === 1,
      });
    } else if (!isEditDialogOpen) {
      setFormData(initialFormData);
    }
  }, [editingEmployment, isEditDialogOpen]);

  const handleAdd = () => {
    const payload: any = {
      employmentStatus: formData.employmentStatus || null,
      employerName: formData.employerName || null,
      employerAbn: formData.employerAbn || null,
      employerIndustry: formData.employerIndustry || null,
      jobTitle: formData.jobTitle || null,
      department: formData.department || null,
      startDate: formData.startDate ? format(formData.startDate, "yyyy-MM-dd") : null,
      endDate: formData.endDate ? format(formData.endDate, "yyyy-MM-dd") : null,
      baseSalary: formData.baseSalary || null,
      salaryFrequency: formData.salaryFrequency || null,
      isCurrent: formData.isCurrent ? 1 : 0,
    };
    addMutation.mutate(payload);
  };

  const handleUpdate = () => {
    if (!editingEmployment) return;
    const payload: any = {
      employmentStatus: formData.employmentStatus || null,
      employerName: formData.employerName || null,
      employerAbn: formData.employerAbn || null,
      employerIndustry: formData.employerIndustry || null,
      jobTitle: formData.jobTitle || null,
      department: formData.department || null,
      startDate: formData.startDate ? format(formData.startDate, "yyyy-MM-dd") : null,
      endDate: formData.endDate ? format(formData.endDate, "yyyy-MM-dd") : null,
      baseSalary: formData.baseSalary || null,
      salaryFrequency: formData.salaryFrequency || null,
      isCurrent: formData.isCurrent ? 1 : 0,
    };
    updateMutation.mutate({ id: editingEmployment.id, payload });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-AU", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (amount: string | null) => {
    if (!amount) return "-";
    const num = parseFloat(amount);
    if (isNaN(num)) return "-";
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const getStatusLabel = (status: string | null) => {
    if (!status) return "-";
    const option = EMPLOYMENT_STATUS_OPTIONS.find(o => o.value === status);
    return option?.label || status.replace(/_/g, " ");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="employment-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-muted-foreground">EMPLOYMENT HISTORY</h3>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setIsAddDialogOpen(true)}
          data-testid="button-add-employment"
        >
          <Plus className="h-3 w-3 mr-1" />
          Add Employment
        </Button>
      </div>

      {employment.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center border rounded-md">
          <Briefcase className="h-10 w-10 text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">No employment records yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Click "Add Employment" to add an employment record
          </p>
        </div>
      ) : (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow className="h-10 bg-muted/50">
                <TableHead className="text-xs font-medium px-2 py-1">Employer Name</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Job Title</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Status</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Start Date</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">End Date</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Base Salary</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1">Current</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1 w-[60px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {employment.map((emp) => (
                <TableRow
                  key={emp.id}
                  className="h-10"
                  data-testid={`row-employment-${emp.id}`}
                >
                  <TableCell className="px-2 py-1 text-sm">
                    {emp.employerName || "-"}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm">
                    {emp.jobTitle || "-"}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm">
                    {getStatusLabel(emp.employmentStatus)}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm">
                    {formatDate(emp.startDate)}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm">
                    {formatDate(emp.endDate)}
                  </TableCell>
                  <TableCell className="px-2 py-1 text-sm">
                    {formatCurrency(emp.baseSalary)}
                  </TableCell>
                  <TableCell className="px-2 py-1">
                    {emp.isCurrent === 1 ? (
                      <Badge variant="secondary" className="text-xs" data-testid={`badge-current-${emp.id}`}>
                        Current
                      </Badge>
                    ) : (
                      <span className="text-sm text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="px-2 py-1">
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenEdit(emp)}
                        data-testid={`button-edit-employment-${emp.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteId(emp.id)}
                        data-testid={`button-delete-employment-${emp.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-3xl" data-testid="dialog-add-employment">
          <DialogHeader>
            <DialogTitle>Add Employment</DialogTitle>
            <DialogDescription>
              Add an employment record for this contact.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Employment Status</Label>
              <Select
                value={formData.employmentStatus}
                onValueChange={(value) => setFormData({ ...formData, employmentStatus: value })}
              >
                <SelectTrigger data-testid="select-employment-status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {EMPLOYMENT_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Employer Name</Label>
              <Input
                value={formData.employerName}
                onChange={(e) => setFormData({ ...formData, employerName: e.target.value })}
                placeholder="Enter employer name"
                data-testid="input-employer-name"
              />
            </div>

            <div className="space-y-2">
              <Label>Employer ABN</Label>
              <Input
                value={formData.employerAbn}
                onChange={(e) => setFormData({ ...formData, employerAbn: e.target.value })}
                placeholder="Enter employer ABN"
                data-testid="input-employer-abn"
              />
            </div>

            <div className="space-y-2">
              <Label>Employer Industry</Label>
              <Input
                value={formData.employerIndustry}
                onChange={(e) => setFormData({ ...formData, employerIndustry: e.target.value })}
                placeholder="Enter industry"
                data-testid="input-employer-industry"
              />
            </div>

            <div className="space-y-2">
              <Label>Job Title</Label>
              <Input
                value={formData.jobTitle}
                onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                placeholder="Enter job title"
                data-testid="input-job-title"
              />
            </div>

            <div className="space-y-2">
              <Label>Department</Label>
              <Input
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                placeholder="Enter department"
                data-testid="input-department"
              />
            </div>

            <div className="flex items-center space-x-2 mb-4">
              <Checkbox
                id="isCurrent"
                checked={formData.isCurrent}
                onCheckedChange={(checked) => {
                  setFormData({
                    ...formData,
                    isCurrent: !!checked,
                    endDate: checked ? undefined : formData.endDate
                  });
                }}
                data-testid="checkbox-is-current"
              />
              <Label htmlFor="isCurrent" className="cursor-pointer">
                Current employment
              </Label>
            </div>

            <div className={cn("grid gap-4", formData.isCurrent ? "grid-cols-1" : "grid-cols-2")}>
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.startDate && "text-muted-foreground"
                      )}
                      data-testid="input-start-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.startDate ? format(formData.startDate, "d MMM yyyy") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.startDate}
                      onSelect={(date) => setFormData({ ...formData, startDate: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {!formData.isCurrent && (
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.endDate && "text-muted-foreground"
                        )}
                        data-testid="input-end-date"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.endDate ? format(formData.endDate, "d MMM yyyy") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.endDate}
                        onSelect={(date) => setFormData({ ...formData, endDate: date })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Base Salary</Label>
                <Input
                  type="number"
                  value={formData.baseSalary}
                  onChange={(e) => setFormData({ ...formData, baseSalary: e.target.value })}
                  placeholder="Enter salary"
                  data-testid="input-base-salary"
                />
              </div>

              <div className="space-y-2">
                <Label>Salary Frequency</Label>
                <Select
                  value={formData.salaryFrequency}
                  onValueChange={(value) => setFormData({ ...formData, salaryFrequency: value })}
                >
                  <SelectTrigger data-testid="select-salary-frequency">
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    {SALARY_FREQUENCY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={addMutation.isPending}
              data-testid="button-confirm-add-employment"
            >
              {addMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Add Employment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Employment Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-3xl" data-testid="dialog-edit-employment">
          <DialogHeader>
            <DialogTitle>Edit Employment</DialogTitle>
            <DialogDescription>
              Update the employment record for this contact.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Employment Status</Label>
              <Select
                value={formData.employmentStatus}
                onValueChange={(value) => setFormData({ ...formData, employmentStatus: value })}
              >
                <SelectTrigger data-testid="edit-select-employment-status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {EMPLOYMENT_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Employer Name</Label>
              <Input
                value={formData.employerName}
                onChange={(e) => setFormData({ ...formData, employerName: e.target.value })}
                placeholder="Enter employer name"
                data-testid="edit-input-employer-name"
              />
            </div>

            <div className="space-y-2">
              <Label>Employer ABN</Label>
              <Input
                value={formData.employerAbn}
                onChange={(e) => setFormData({ ...formData, employerAbn: e.target.value })}
                placeholder="Enter employer ABN"
                data-testid="edit-input-employer-abn"
              />
            </div>

            <div className="space-y-2">
              <Label>Employer Industry</Label>
              <Input
                value={formData.employerIndustry}
                onChange={(e) => setFormData({ ...formData, employerIndustry: e.target.value })}
                placeholder="Enter industry"
                data-testid="edit-input-employer-industry"
              />
            </div>

            <div className="space-y-2">
              <Label>Job Title</Label>
              <Input
                value={formData.jobTitle}
                onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                placeholder="Enter job title"
                data-testid="edit-input-job-title"
              />
            </div>

            <div className="space-y-2">
              <Label>Department</Label>
              <Input
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                placeholder="Enter department"
                data-testid="edit-input-department"
              />
            </div>

            <div className="flex items-center space-x-2 mb-4">
              <Checkbox
                id="edit-isCurrent"
                checked={formData.isCurrent}
                onCheckedChange={(checked) => {
                  setFormData({
                    ...formData,
                    isCurrent: !!checked,
                    endDate: checked ? undefined : formData.endDate
                  });
                }}
                data-testid="edit-checkbox-is-current"
              />
              <Label htmlFor="edit-isCurrent" className="cursor-pointer">
                Current employment
              </Label>
            </div>

            <div className={cn("grid gap-4", formData.isCurrent ? "grid-cols-1" : "grid-cols-2")}>
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.startDate && "text-muted-foreground"
                      )}
                      data-testid="edit-input-start-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.startDate ? format(formData.startDate, "d MMM yyyy") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.startDate}
                      onSelect={(date) => setFormData({ ...formData, startDate: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {!formData.isCurrent && (
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.endDate && "text-muted-foreground"
                        )}
                        data-testid="edit-input-end-date"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.endDate ? format(formData.endDate, "d MMM yyyy") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.endDate}
                        onSelect={(date) => setFormData({ ...formData, endDate: date })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Base Salary</Label>
                <Input
                  type="number"
                  value={formData.baseSalary}
                  onChange={(e) => setFormData({ ...formData, baseSalary: e.target.value })}
                  placeholder="Enter salary"
                  data-testid="edit-input-base-salary"
                />
              </div>

              <div className="space-y-2">
                <Label>Salary Frequency</Label>
                <Select
                  value={formData.salaryFrequency}
                  onValueChange={(value) => setFormData({ ...formData, salaryFrequency: value })}
                >
                  <SelectTrigger data-testid="edit-select-salary-frequency">
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    {SALARY_FREQUENCY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseEditDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={updateMutation.isPending}
              data-testid="button-confirm-update-employment"
            >
              {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Update Employment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Employment</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this employment record? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete-employment"
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
